import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Popover,
    List,
    ListItem,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { RemoveCircle, AddCircle } from '@material-ui/icons';
import { EmployeeBidProfileWarningType } from '../../Enums';
import './ImportWarning.scss';

const AddedLineWarning = (props) => (
    <ListItem {...props}>
        <AddCircle style={{ color: 'green', paddingRight: '2px' }} />
        { props.children }
    </ListItem>
);

const RemovedLineWarning = (props) => (
    <ListItem {...props}>
        <RemoveCircle style={{ color: 'red', paddingRight: '2px' }} />
        { props.children }
    </ListItem>
);

/**
 * @typedef {{ warnings: EmployeeBidProfile.IEmployeeBidProfileWarning[], isMini: boolean }} ImportWarningProps
 * @param {ImportWarningProps} props
 */
function ImportWarning(props) {
    const { warnings = [], isMini = false } = props;
    const { t } = useTranslation('shiftBidding');
    /** @type {ShiftBidding.Store} */
    // @ts-ignore
    const { publicConfigs } = useSelector((state) => state);
    const { isUseUkgReadyTerminology } = publicConfigs;

    /**
     * @type {[HTMLInputElement | null, Function]}
     */
    const [anchorEl, setAnchorEl] = useState(null);

    const schedulePatternLabel = t(isUseUkgReadyTerminology
        ? 'common.terminology.schedulePattern_ukgReady'
        : 'common.terminology.schedulePattern');

    /**
     * @param {React.MouseEvent<HTMLButtonElement, MouseEvent>} e
     */
    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'import-warnings-popover' : undefined;
    const hasWarnings = !!warnings.length;
    const buttonStyles = isMini ? '' : 'iw-button-container';
    const listStyles = isMini ? 'iw-warnings-list-mini' : '';

    return (
        <div className="iw-container">
            <Button
                onClick={handleClick}
                className={buttonStyles}
                disabled={!hasWarnings}
            >
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    size="lg"
                    color={hasWarnings ? '#FFB020' : 'grey'}
                />
                { !isMini && (
                    <span className="iw-label-text">
                        {`${warnings.length} ${warnings.length === 1 ? t('common.warning') : t('common.warnings')}`}
                    </span>
                )}
            </Button>
            <Popover
                id={id}
                classes={{
                    paper: 'iw-popover-paper',
                }}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <List className={listStyles}>
                    {
                        warnings.map((x) => {
                            if (x.type === EmployeeBidProfileWarningType.SchedulePatternAdded) {
                                return (
                                    <AddedLineWarning
                                        key={`${x.type}_${x.details}`}
                                        className="iw-warnings-list-item"
                                    >
                                        <span>{schedulePatternLabel}</span>
                                        <span className="iw-label-schedule-pattern-name">{x.details}</span>
                                        <span>{t('employee.importWarning.wasAdded')}</span>
                                    </AddedLineWarning>
                                );
                            }
                            if (x.type === EmployeeBidProfileWarningType.SchedulePatternRemoved) {
                                return (
                                    <RemovedLineWarning
                                        key={`${x.type}_${x.details}`}
                                        className="iw-warnings-list-item"
                                    >
                                        <span>{schedulePatternLabel}</span>
                                        <span className="iw-label-schedule-pattern-name">{x.details}</span>
                                        <span>{t('employee.importWarning.wasRemoved')}</span>
                                    </RemovedLineWarning>
                                );
                            }
                            return null;
                        })
                    }
                </List>
            </Popover>
        </div>

    );
}

export default ImportWarning;
